import * as React from 'react';
import { CalendarIcon, X } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/core/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Calendar } from '@/shared/components/ui/Calendar';
import { floatingInputVariants, floatingLabelVariants } from './FloatingLabelInput';
import type { VariantProps } from 'class-variance-authority';
import { useDateFormat } from '@/shared/hooks/useDateFormat';
import { parseDatePickerValue } from '@/shared/lib/datetime/instant';

interface FloatingLabelDatePickerProps extends VariantProps<typeof floatingInputVariants> {
  label: string;
  id: string;
  value?: string | null;
  onChange: (value: string | null) => void;
  invalid?: boolean;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  'data-testid'?: string;
}

export function FloatingLabelDatePicker({
  label,
  id,
  value,
  onChange,
  invalid = false,
  disabled = false,
  minDate,
  maxDate,
  variant,
  className,
  'data-testid': testId,
}: FloatingLabelDatePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const { dateFormat } = useDateFormat();

  const selectedDate = value ? parseDatePickerValue(value) : undefined;
  const isFloating = isOpen || !!value;

  const handleSelect = (date: Date | undefined) => {
    // Full ISO datetime, not date-only — some consumers' backend contracts
    // require it (e.g. project start/end dates); date-only consumers slice
    // to `.slice(0, 10)` themselves. Known caveat: this converts through
    // UTC, so it can shift the selected day by one in negative-offset
    // timezones — callers that need the exact local day should re-derive it
    // from local `Date` fields rather than trust this value's date portion.
    onChange(date ? date.toISOString() : null);
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <div className="relative w-full flex items-center">
      {/* `modal` keeps an outside click from dismissing an enclosing Dialog
          along with the calendar — a non-modal popover leaves the Dialog's
          dismissable layer active underneath it. */}
      <Popover open={isOpen} onOpenChange={setIsOpen} modal>
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            disabled={disabled}
            aria-invalid={invalid}
            data-testid={testId}
            className={cn(
              floatingInputVariants({ variant }),
              'text-left px-3 flex items-center gap-2',
              invalid && 'outline-destructive ring-2 ring-destructive/20',
              className,
            )}
          >
            <CalendarIcon className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="flex-1 text-foreground text-sm">
              {value ? format(parseDatePickerValue(value), dateFormat) : ''}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            selected={selectedDate}
            onSelect={handleSelect}
            fromDate={minDate}
            toDate={maxDate}
            mode="single"
          />
        </PopoverContent>
      </Popover>

      <label
        htmlFor={id}
        className={cn(
          floatingLabelVariants({ variant, floating: isFloating }),
          'pointer-events-none',
        )}
      >
        {label}
      </label>

      {value && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          tabIndex={-1}
          className="absolute right-3 flex items-center text-muted-foreground hover:text-destructive transition-colors"
          aria-label="Clear date"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
